import React from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Container from '@amzn/awsui-components-console/container';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Badge from '@amzn/awsui-components-console/badge';
import Icon from '@amzn/awsui-components-console/icon';
import { features, PILOT_URL, SURVEY_URL, userTypes } from '../../constants';
import Roadmap from './Roadmap';
import AstVsLangHub from './AstVsLangHub';
import tsLogo from 'src/images/ts-logo.svg';
import styles from './MainContent.module.scss';

/** Number of feature cards in the 3-column top grid; remainder goes in the 2-column bottom grid */
const FEATURES_TOP_ROW_COUNT = 9;

const MainContent = () => (
  <div className={styles.wrapper}>
    <SpaceBetween size="xl">
      {/* Hero */}
      <section aria-label="Welcome to LangHub">
        <Container>
          <Box
            textAlign="center"
            padding={{ vertical: 'xl', horizontal: 'xxl' }}
          >
            <SpaceBetween size="m" alignItems="center">
              <img src={tsLogo} height={110} alt="" />
              <Box variant="h1" fontSize="display-l" fontWeight="bold">
                Welcome to LangHub
              </Box>
              <Box
                variant="h2"
                fontSize="heading-xl"
                fontWeight="normal"
                color="inherit"
              >
                <span className={styles.subtitle}>
                  Amazon&apos;s CMS for translations
                </span>
              </Box>
              <Box variant="p" fontSize="body-m" color="text-body-secondary">
                Create, manage, review, and track your string translations — all
                in one place.
              </Box>
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <Button
                  variant="primary"
                  iconName="arrow-right"
                  iconAlign="right"
                  href={PILOT_URL}
                  target="_blank"
                >
                  Join Our Pilot
                </Button>
                <Button
                  variant="normal"
                  iconName="external"
                  iconAlign="right"
                  href={SURVEY_URL}
                  target="_blank"
                >
                  Take Quick Survey
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Box>
        </Container>
      </section>

      {/* Value Proposition */}
      <section aria-label="What is LangHub">
        <Container>
          <SpaceBetween size="l">
            <SpaceBetween size="xs">
              <Box
                variant="h2"
                fontSize="heading-xl"
                fontWeight="bold"
                textAlign="center"
              >
                What is LangHub — and why it helps
              </Box>
              <Box variant="p" color="text-body-secondary" textAlign="center">
                Translation Services&apos; unified CMS for string translations
                across Amazon&apos;s 127 locales — one platform for all your
                translation needs.
              </Box>
            </SpaceBetween>
            <div className={styles.contentNarrow}>
              <ColumnLayout columns={3} variant="default">
                <Container fitHeight={true}>
                  <SpaceBetween size="s">
                    <Box textAlign="center">
                      <Icon name="arrow-up" size="medium" variant="link" />
                    </Box>
                    <Box
                      variant="h3"
                      fontSize="heading-m"
                      fontWeight="bold"
                      textAlign="center"
                    >
                      Scale quickly — no tooling required
                    </Box>
                    <Box
                      variant="p"
                      color="text-body-secondary"
                      textAlign="center"
                    >
                      LangHub connects you directly to our LLM and quality
                      models — no tooling to build or API onboarding to
                      navigate.
                    </Box>
                  </SpaceBetween>
                </Container>
                <Container fitHeight={true}>
                  <SpaceBetween size="s">
                    <Box textAlign="center">
                      <Icon name="ticket" size="medium" variant="success" />
                    </Box>
                    <Box
                      variant="h3"
                      fontSize="heading-m"
                      fontWeight="bold"
                      textAlign="center"
                    >
                      Reduced translation costs
                    </Box>
                    <Box
                      variant="p"
                      color="text-body-secondary"
                      textAlign="center"
                    >
                      Through our LLM models and economies of scale, we offer
                      the lowest translation cost across Amazon.
                    </Box>
                  </SpaceBetween>
                </Container>
                <Container fitHeight={true}>
                  <SpaceBetween size="s">
                    <Box textAlign="center">
                      <Icon name="refresh" size="medium" variant="link" />
                    </Box>
                    <Box
                      variant="h3"
                      fontSize="heading-m"
                      fontWeight="bold"
                      textAlign="center"
                    >
                      End-to-end customer support
                    </Box>
                    <Box
                      variant="p"
                      color="text-body-secondary"
                      textAlign="center"
                    >
                      When you join us, we manage all your translation needs —
                      it&apos;s always Day 1 with us.
                    </Box>
                  </SpaceBetween>
                </Container>
              </ColumnLayout>
            </div>
          </SpaceBetween>
        </Container>
      </section>

      {/* Features */}
      <section
        id="features"
        aria-label="Features"
        className={styles.anchorSection}
      >
        <Container>
          <SpaceBetween size="l">
            <SpaceBetween size="xs">
              <Box
                variant="h2"
                fontSize="heading-xl"
                fontWeight="bold"
                textAlign="center"
              >
                How LangHub enables end-to-end translation management
              </Box>
              <Box variant="p" color="text-body-secondary" textAlign="center">
                The features that power LangHub
              </Box>
            </SpaceBetween>
            <div className={styles.contentNarrow}>
              <ColumnLayout columns={3} variant="default">
                {features.slice(0, FEATURES_TOP_ROW_COUNT).map((f) => (
                  <Container key={f.title} fitHeight={true}>
                    <SpaceBetween size="xs">
                      <Box>
                        <Icon name={f.iconName} size="normal" variant="link" />{' '}
                        <Box variant="strong" display="inline">
                          {f.title}
                        </Box>
                        {f.badge && (
                          <>
                            {' '}
                            <Badge color="blue">{f.badge}</Badge>
                          </>
                        )}
                      </Box>
                      <Box
                        variant="p"
                        fontSize="body-m"
                        color="text-body-secondary"
                      >
                        {f.description}
                      </Box>
                    </SpaceBetween>
                  </Container>
                ))}
              </ColumnLayout>
              <div className={styles.contentNarrower}>
                <ColumnLayout columns={2} variant="default">
                  {features.slice(FEATURES_TOP_ROW_COUNT).map((f) => (
                    <Container key={f.title} fitHeight={true}>
                      <SpaceBetween size="xs">
                        <Box>
                          <Icon
                            name={f.iconName}
                            size="normal"
                            variant="link"
                          />{' '}
                          <Box variant="strong" display="inline">
                            {f.title}
                          </Box>
                          {f.badge && (
                            <>
                              {' '}
                              <Badge color="blue">{f.badge}</Badge>
                            </>
                          )}
                        </Box>
                        <Box
                          variant="p"
                          fontSize="body-m"
                          color="text-body-secondary"
                        >
                          {f.description}
                        </Box>
                      </SpaceBetween>
                    </Container>
                  ))}
                </ColumnLayout>
              </div>
            </div>
          </SpaceBetween>
        </Container>
      </section>

      {/* Who Can Use */}
      <section
        id="who"
        aria-label="Who can use LangHub"
        className={styles.anchorSection}
      >
        <Container>
          <SpaceBetween size="l">
            <SpaceBetween size="xs">
              <Box
                variant="h2"
                fontSize="heading-xl"
                fontWeight="bold"
                textAlign="center"
                id="who-heading"
              >
                Who can use LangHub
              </Box>
              <Box variant="p" color="text-body-secondary" textAlign="center">
                LangHub serves a wide range of teams across Amazon&apos;s
                localization ecosystem.
              </Box>
            </SpaceBetween>
            <ColumnLayout columns={4} variant="default">
              {userTypes.map((u) => (
                <Container key={u.title} fitHeight={true}>
                  <SpaceBetween size="s">
                    <SpaceBetween
                      direction="horizontal"
                      size="s"
                      alignItems="center"
                    >
                      {u.iconSvg ? (
                        <img src={u.iconSvg} alt="" width={24} height={24} />
                      ) : (
                        u.iconName && (
                          <Icon
                            name={u.iconName}
                            size="normal"
                            variant="link"
                          />
                        )
                      )}
                      <Box variant="h3" fontSize="heading-m" fontWeight="bold">
                        {u.title}
                      </Box>
                    </SpaceBetween>
                    <Box variant="p" color="text-body-secondary">
                      {u.description}
                    </Box>
                  </SpaceBetween>
                </Container>
              ))}
            </ColumnLayout>
          </SpaceBetween>
        </Container>
      </section>

      {/* Roadmap */}
      <Roadmap />

      {/* AST vs LangHub */}
      <AstVsLangHub />
    </SpaceBetween>
  </div>
);

export default MainContent;
